import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Trash2 } from "lucide-react";
import { adminApi, errMsg, inr, PLACEHOLDER } from "@/lib/api";
import { useSite } from "@/lib/store";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Btn, Field, PageHead, Panel, inputCls } from "@/admin/ui";

const EMPTY = { sku: "", artist: "", title: "", edition: "", format: "LP", genres: [], price: "", regular_price: "", stock: 0, image: "", description: "", tracklist: "", label: "", release_year: "", meta_title: "", meta_description: "", meta_keywords: "", active: true, express_delivery: false };

function ProductForm({ product, onClose, onSaved }) {
  const { site } = useSite();
  const [f, setF] = useState({ ...EMPTY, ...product, regular_price: product?.regular_price ?? "", genres: product?.genres || [] });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const genreOpts = (site?.menu || []).filter((m) => m.type === "genre").map((m) => m.value);
  const toggleGenre = (g) => setF({ ...f, genres: f.genres.includes(g) ? f.genres.filter((x) => x !== g) : [...f.genres, g] });
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, price: Number(f.price || 0), regular_price: f.regular_price === "" ? null : Number(f.regular_price), stock: Number(f.stock || 0) };
      const r = product?.id ? await adminApi.put(`/products/${product.id}`, body) : await adminApi.post("/products", body);
      toast.success("Product saved");
      onSaved(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const inp = (k, props = {}) => <input value={f[k]} onChange={set(k)} className={inputCls} data-testid={`product-form-${k}`} {...props} />;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl rounded-none max-h-[92vh] overflow-y-auto" data-testid="product-form-dialog">
        <DialogHeader><DialogTitle className="font-head text-2xl">{product?.id ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Catalogue number">{inp("sku")}</Field>
          <div className="sm:col-span-2"><Field label="Artist">{inp("artist")}</Field></div>
          <div className="sm:col-span-2"><Field label="Title">{inp("title")}</Field></div>
          <Field label="Format">{inp("format", { placeholder: "LP, 2LP, 3LP" })}</Field>
          <div className="sm:col-span-2"><Field label="Edition">{inp("edition", { placeholder: "e.g. Red/Ocean Blue Vinyl" })}</Field></div>
          <Field label="Stock">{inp("stock", { type: "number", min: 0 })}</Field>
          <Field label="Price (₹)">{inp("price", { type: "number", min: 0, step: "0.01" })}</Field>
          <Field label="Old price (₹)" hint="Shown crossed out; leave blank for none">{inp("regular_price", { type: "number", min: 0, step: "0.01" })}</Field>
          <Field label="Label">{inp("label")}</Field>
          <div className="sm:col-span-2"><Field label="Cover image URL">{inp("image")}</Field></div>
          <Field label="Release year">{inp("release_year")}</Field>
        </div>
        <Field label="Genres">
          <div className="flex flex-wrap gap-2">
            {[...new Set([...genreOpts, ...f.genres])].map((g) => (
              <button type="button" key={g} onClick={() => toggleGenre(g)} className={`px-2.5 py-1 text-xs border ${f.genres.includes(g) ? "bg-black text-white border-black" : "border-zinc-300"}`}>{g}</button>
            ))}
          </div>
        </Field>
        <Field label="Description"><textarea value={f.description} onChange={set("description")} rows={3} className={`${inputCls} h-auto py-2`} /></Field>
        <Field label="Tracklist"><textarea value={f.tracklist} onChange={set("tracklist")} rows={3} className={`${inputCls} h-auto py-2`} /></Field>
        <div className="border border-zinc-200 p-4 space-y-3 bg-zinc-50" data-testid="product-form-seo">
          <p className="font-bold text-sm">SEO <span className="font-normal text-xs text-zinc-500">– leave blank to use the default product metas from Website content</span></p>
          <Field label="Meta title">{inp("meta_title")}</Field>
          <Field label="Meta description"><textarea value={f.meta_description} onChange={set("meta_description")} rows={2} className={`${inputCls} h-auto py-2`} data-testid="product-form-meta_description" /></Field>
          <Field label="Meta keywords">{inp("meta_keywords")}</Field>
        </div>
        <label className="flex items-center gap-3 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /> Visible on website</label>
        <label className="flex items-center gap-3 text-sm"><Switch checked={!!f.express_delivery} onCheckedChange={(v) => setF({ ...f, express_delivery: v })} data-testid="product-form-express" /> Express delivery (shows the express delivery text from Shipping)</label>
        <div className="flex justify-end gap-2"><Btn variant="outline" onClick={onClose}>Cancel</Btn><Btn variant="red" onClick={save} disabled={busy} data-testid="product-form-save">{busy ? "Saving…" : "Save product"}</Btn></div>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteConfirm({ preview, onCancel, onConfirm, busy }) {
  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-lg rounded-none" data-testid="delete-confirm-dialog">
        <DialogHeader><DialogTitle className="font-head text-xl">Delete {preview.count} product{preview.count === 1 ? "" : "s"}?</DialogTitle></DialogHeader>
        <p className="text-sm text-red">This permanently removes them from the website. This can't be undone.</p>
        <ul className="max-h-60 overflow-y-auto text-sm border border-zinc-200 divide-y divide-zinc-100">
          {preview.items.slice(0, 100).map((p) => <li key={p.id || p.sku} className="px-3 py-1.5"><span className="font-arial text-zinc-500">{p.sku}</span> · {p.artist} – {p.title}</li>)}
        </ul>
        {preview.count > 100 && <p className="text-xs text-zinc-500">…and {preview.count - 100} more</p>}
        <div className="flex justify-end gap-2"><Btn variant="outline" onClick={onCancel}>Cancel</Btn><Btn variant="red" disabled={busy} onClick={onConfirm} data-testid="delete-confirm-button">{busy ? "Deleting…" : "Yes, delete"}</Btn></div>
      </DialogContent>
    </Dialog>
  );
}

export default function Products() {
  const [q, setQ] = useState("");
  const [stock, setStock] = useState("");
  const [kind, setKind] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [edit, setEdit] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    adminApi.get("/products", { params: { q, page, stock, kind } }).then((r) => setData(r.data));
  }, [q, page, stock, kind]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const toggle = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  const pageIds = data?.items.map((p) => p.id) || [];
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => sel.includes(id));
  const askDelete = async () => setPreview((await adminApi.post("/products/delete-preview", { ids: sel })).data);
  const doDelete = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post("/products/delete", { ids: sel });
      toast.success(`${r.data.deleted} products deleted`);
      setSel([]);
      setPreview(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const setExpress = async (express) => {
    try {
      const r = await adminApi.post("/products/delivery", { ids: sel, express });
      toast.success(`${r.data.updated} products ${express ? "set to express" : "set to standard"} delivery`);
      setSel([]);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHead title="Products" desc={data ? `${data.total.toLocaleString("en-IN")} products in the catalogue` : ""}>
        {sel.length > 0 && <Btn variant="outline" onClick={() => setExpress(true)} data-testid="products-express-on">Express delivery</Btn>}
        {sel.length > 0 && <Btn variant="outline" onClick={() => setExpress(false)} data-testid="products-express-off">Standard delivery</Btn>}
        {sel.length > 0 && <Btn variant="outline" onClick={askDelete} data-testid="products-delete-selected"><Trash2 className="w-4 h-4" /> Delete {sel.length} selected</Btn>}
        <Btn onClick={() => setEdit({})} data-testid="products-add"><Plus className="w-4 h-4" /> Add product</Btn>
      </PageHead>
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-zinc-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search artist, title or catalogue number" className={`${inputCls} pl-9`} data-testid="products-search" />
        </div>
        <div className="flex border border-zinc-300 text-xs" data-testid="products-kind-filter">
          {[["", "All"], ["records", "Records"], ["gear", "Turntables & accessories"]].map(([v, l]) => (
            <button key={v || "all"} onClick={() => { setKind(v); setPage(1); }} data-testid={`products-kind-${v || "all"}`}
              className={`px-3 h-10 ${kind === v ? "bg-black text-white" : "bg-white hover:bg-zinc-50"}`}>{l}</button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm"><Switch checked={stock === "out"} onCheckedChange={(v) => { setStock(v ? "out" : ""); setPage(1); }} data-testid="products-out-of-stock-filter" /> Out of stock only</label>
      </div>
      <Panel className="p-0 sm:p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="products-table">
          <thead><tr className="text-left label-caps text-[10px] text-zinc-500 border-b border-zinc-200">
            <th className="px-4 py-3"><Checkbox checked={allOnPage} onCheckedChange={(v) => setSel(v ? [...new Set([...sel, ...pageIds])] : sel.filter((x) => !pageIds.includes(x)))} data-testid="products-select-page" /></th>
            {["", "Cat. no.", "Artist / title", "Format", "Price", "Stock", ""].map((h, i) => <th key={i} className="px-3 py-3 font-normal">{h}</th>)}
          </tr></thead>
          <tbody>
            {data?.items.map((p) => (
              <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`product-row-${p.sku}`}>
                <td className="px-4 py-2"><Checkbox checked={sel.includes(p.id)} onCheckedChange={() => toggle(p.id)} data-testid={`product-select-${p.sku}`} /></td>
                <td className="px-3 py-2"><img src={p.image || PLACEHOLDER} alt="" className="w-10 h-10 object-cover border border-zinc-200" /></td>
                <td className="px-3 py-2 font-arial text-xs">{p.sku}</td>
                <td className="px-3 py-2"><b className="font-arial uppercase text-xs">{p.artist}</b><div className="text-zinc-600">{p.title}{p.edition && <span className="text-zinc-400"> · {p.edition}</span>}</div>{p.express_delivery && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-red-100 text-red-700" data-testid={`product-express-${p.sku}`}>Express</span>}</td>
                <td className="px-3 py-2 font-arial text-xs">{p.format}</td>
                <td className="px-3 py-2 font-arial">{inr(p.price)}{p.regular_price && <div className="text-xs text-zinc-400 line-through">{inr(p.regular_price)}</div>}</td>
                <td className={`px-3 py-2 font-arial ${p.stock ? "" : "text-red font-bold"}`}>{p.stock}</td>
                <td className="px-3 py-2"><Btn variant="outline" className="h-8 px-3" onClick={() => setEdit(p)} data-testid={`product-edit-${p.sku}`}>Edit</Btn></td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.items.length === 0 && <p className="p-10 text-center text-zinc-500">No products found.</p>}
      </Panel>
      {data?.pages > 1 && (
        <div className="flex gap-3 items-center mt-4 text-sm">
          <Btn variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</Btn>
          <span>Page {page} / {data.pages.toLocaleString("en-IN")}</span>
          <Btn variant="outline" disabled={page >= data.pages} onClick={() => setPage(page + 1)}>Next</Btn>
        </div>
      )}
      {edit && <ProductForm product={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {preview && <DeleteConfirm preview={preview} busy={busy} onCancel={() => setPreview(null)} onConfirm={doDelete} />}
    </div>
  );
}
